import { differenceInDays, format, addDays } from 'date-fns';
import * as stores from '../stores';
import { getCurrencySymbol } from '../utils/pricing';
import { isDevelopment } from '../config/env';

interface DevOverrides {
  isSubscribed: boolean;
  isTrialPeriod: boolean;
  hasBillingIssue: boolean;
  planType: 'yearly' | 'weekly';
  expirationDate: Date;
}

/**
 * Dev-only URL param overrides for testing subscription states.
 * Only works in development mode.
 *
 * Usage:
 *   ?sub=pro          - Simulate active subscriber
 *   ?sub=trial        - Simulate trial user
 *   ?sub=billing      - Simulate subscriber with billing issue
 *   ?sub=none         - Simulate non-subscriber (default)
 *   ?plan=yearly      - Set plan type (default: weekly)
 *
 * To test premium model limits, edit localStorage directly:
 *   localStorage.setItem('premiumModelUsage', JSON.stringify({date: '2026-01-28', count: 19}))
 */
const getDevOverrides = (): DevOverrides | null => {
  if (!isDevelopment()) return null;

  const params = new URLSearchParams(window.location.search);
  const sub = params.get('sub');
  if (!sub) return null;

  const plan = params.get('plan') || 'weekly';
  const isYearly = plan === 'yearly';

  if (sub === 'pro') {
    return {
      isSubscribed: true,
      isTrialPeriod: false,
      hasBillingIssue: false,
      planType: (isYearly ? 'yearly' : 'weekly') as 'yearly' | 'weekly',
      expirationDate: addDays(new Date(), 30),
    };
  }

  if (sub === 'trial') {
    return {
      isSubscribed: true,
      isTrialPeriod: true,
      hasBillingIssue: false,
      planType: (isYearly ? 'yearly' : 'weekly') as 'yearly' | 'weekly',
      expirationDate: addDays(new Date(), 2),
    };
  }

  if (sub === 'billing') {
    return {
      isSubscribed: true,
      isTrialPeriod: false,
      hasBillingIssue: true,
      planType: (isYearly ? 'yearly' : 'weekly') as 'yearly' | 'weekly',
      expirationDate: addDays(new Date(), 4),
    };
  }

  return null;
};

/**
 * Get subscription status details from RevenueCat CustomerInfo
 */
const useSubscriptionStatus = () => {
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const offerings = stores.Purchase.useState((s) => s.offerings);
  const entitlement = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier];

  // Check for dev overrides first
  const devOverrides = getDevOverrides();

  const isSubscribed = devOverrides?.isSubscribed ?? entitlement?.isActive ?? false;
  const isTrialPeriod = devOverrides?.isTrialPeriod ?? entitlement?.periodType === 'TRIAL';
  const expirationDate =
    devOverrides?.expirationDate ?? (entitlement?.expirationDate ? new Date(entitlement.expirationDate) : null);
  const productIdentifier = entitlement?.productIdentifier ?? '';

  // Determine plan type from product identifier (or use dev override)
  const isYearlyPlan =
    productIdentifier.toLowerCase().includes('annual') || productIdentifier.toLowerCase().includes('yearly');
  const planType: 'yearly' | 'weekly' | null =
    devOverrides?.planType ?? (isSubscribed ? (isYearlyPlan ? 'yearly' : 'weekly') : null);

  // Get prices from offerings
  const yearlyPrice = offerings?.current?.annual?.product?.price ?? 104;
  const weeklyPrice = offerings?.current?.weekly?.product?.price ?? 5;

  // Get currency symbol from currencyCode (more reliable than parsing priceString)
  const currencyCode =
    offerings?.current?.annual?.product?.currencyCode || offerings?.current?.weekly?.product?.currencyCode;
  const currencySymbol = getCurrencySymbol(currencyCode);

  const getPrice = (): string => {
    if (!planType) return '';
    if (planType === 'yearly') {
      return `${currencySymbol}${yearlyPrice}/year`;
    } else {
      return `${currencySymbol}${weeklyPrice}/week`;
    }
  };

  // Calculate days left in trial
  // Note: differenceInDays rounds toward zero, so 23 hours left shows as 0 days
  const trialDaysLeft = isTrialPeriod && expirationDate ? Math.max(0, differenceInDays(expirationDate, new Date())) : 0;

  // User-friendly text for trial days remaining
  const getTrialDaysLeftText = (): string => {
    if (trialDaysLeft === 0) return 'Last day of trial';
    if (trialDaysLeft === 1) return '1 day left';
    return `${trialDaysLeft} days left`;
  };

  // Format renewal/expiration date
  const formattedDate = expirationDate ? format(expirationDate, 'MMM d, yyyy') : null;

  const hasBillingIssue = devOverrides?.hasBillingIssue ?? !!entitlement?.billingIssueDetectedAt;

  return {
    isSubscribed,
    isTrialPeriod,
    hasBillingIssue,
    planType,
    price: getPrice(),
    yearlyPrice,
    weeklyPrice,
    currencySymbol,
    trialDaysLeft,
    trialDaysLeftText: getTrialDaysLeftText(),
    expirationDate,
    formattedDate,
  };
};

export default useSubscriptionStatus;
